import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTokenManager } from "contexts/TokenManagerContext";
import envs from "config/envs";
import { isArtistSurfacePath } from "core/auth/artistPortalPaths";

function consumerAppOrigin(): string | null {
  try {
    return new URL(envs.streamerUri).origin;
  } catch {
    return null;
  }
}

/**
 * Forces artists onto `envs.artistPortalUri` when the SPA is served from the
 * consumer host (e.g. app.starfarm.com). Complements `ArtistFlowHandler`; runs
 * as soon as Router + TokenManager are available so older cached scripts cannot
 * strand artists on the wrong origin.
 *
 * Also redirects when the path is an artist surface (`/artist/*`, `/upload/*`)
 * and the user has a token, even if legacy "loop breaker" scripts cleared
 * `localStorage.isArtist` on the main domain.
 *
 * Skips `/admin` so dashboard admins stay on the same host they used to sign in.
 */
const SyncArtistSubdomain: React.FC = () => {
  const { isArtist, getToken } = useTokenManager();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;

    const token = getToken();
    const onConsumer =
      typeof window !== "undefined" &&
      consumerAppOrigin() !== null &&
      window.location.origin === consumerAppOrigin();

    const shouldMove =
      !!token &&
      onConsumer &&
      (isArtist || isArtistSurfacePath(location.pathname));

    if (!shouldMove) return;

    try {
      const targetOrigin = new URL(envs.artistPortalUri).origin;
      if (window.location.origin === targetOrigin) return;
      window.location.replace(
        `${envs.artistPortalUri}${location.pathname}${location.search}`
      );
    } catch {
      /* ignore bad env URL */
    }
    // getToken identity changes each render; token is read inside the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on route / role
  }, [isArtist, location.pathname, location.search]);

  return null;
};

export default SyncArtistSubdomain;
